import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import styled from 'styled-components';

import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { HyperLink } from '@/pages/EditorialDistributionQueue/components/TableRow';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';

const Dialog = styled(StyledDialog)`
  width: 1160px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  height: 614px;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const StyledTable = styled(Table)`
  th,
  td {
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

interface Props {
  onClose: VoidFunction;
  post: EditorialFacebookPostResource;
}

const CommentsModal: FC<Props> = ({ onClose, post }) => {
  const toasts = useToasts();

  const queryClient = useQueryClient();

  const {
    data: comments,
    isLoading,
    isError,
  } = useQuery(
    [
      'fb_post_comments_intent',
      {
        postId: post.id,
      },
    ],
    () =>
      api.get<
        never,
        Array<{
          id: number;
          externalId: string;
          message: string;
          scores: Record<string, number>;
        }>
      >('/v1/editorial/fb-comments-intent', {
        params: {
          'filter[post_id]': post.id,
        },
      }),
  );

  const onDelete = async (id: number) => {
    const toastId = toasts.create('loading', 'Deleting...');

    try {
      await api.delete(`/v1/editorial/fb-comments-intent/${id}`);

      await queryClient.invalidateQueries([
        'fb_post_comments_intent',
        {
          postId: post.id,
        },
      ]);

      toasts.success(toastId, 'Deleted!');
    } catch (err) {
      toasts.error(toastId, 'Failed!');
    }
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Flagged comments</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <ScrollableTable>
            <StyledTable $fixed>
              <thead>
                <tr>
                  <Th>Link</Th>
                  <Th>Message</Th>
                  <Th>Scores</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                <TableContent
                  isLoading={isLoading}
                  isError={isError}
                  hasData={!!comments?.length}
                  colSpan={3}
                >
                  {comments?.map((c) => (
                    <tr key={c.id}>
                      <Td>
                        {' '}
                        <HyperLink
                          href={`https://facebook.com/${c.externalId}`}
                        >
                          <HyperlinkIcon />
                          <span>{c.externalId}</span>
                        </HyperLink>
                      </Td>
                      <Td>{c.message}</Td>
                      <Td>
                        {Object.keys(c.scores).map((key, i) => (
                          <div key={i}>{`${key}: ${c.scores[key]}`}</div>
                        ))}
                      </Td>
                      <Td>
                        <Button
                          type="button"
                          color="danger"
                          small
                          onClick={() => onDelete(c.id)}
                        >
                          Delete
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </TableContent>
              </tbody>
            </StyledTable>
          </ScrollableTable>
        </Body>
        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default CommentsModal;
